import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { TrustFooter } from "@/components/TrustFooter";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Target, BadgeCheck, FileCheck,
  Building2, Users, Briefcase, Shield, Clock, Inbox, Send,
  Stethoscope, Monitor, BarChart3, Landmark, Building, Crown,
  Globe, Lock, UserCheck, Zap, Menu, X
} from "lucide-react";
import { useState, useEffect } from "react";
import vexisLogo from "@assets/vexis-logo-2026.jpg";

export default function Services() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = "Assessment Services | Vexis";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#05070a", color: "#ffffff" }}>
      <nav className="sticky top-0 z-50" style={{ backgroundColor: "rgba(5,7,10,0.92)", borderBottom: "1px solid rgba(99,102,241,0.15)", backdropFilter: "blur(20px)" }}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link href="/" className="flex items-center gap-3">
            <img src={vexisLogo} alt="VEXIS Talent Agency" className="h-10 md:h-12 w-auto" data-testid="logo-services" />
            <span className="text-white font-bold tracking-widest text-sm">VEXIS</span>
          </Link>
          <div className="hidden lg:flex items-center gap-8">
            {[
              { href: "/", label: "Home" },
              { href: "/about", label: "About" },
              { href: "/services", label: "Services" },
              { href: "/methodology", label: "Methodology" },
            ].map(({ href, label }) => (
              <Link key={label} href={href} className="text-sm font-medium transition-colors"
                style={{ color: href === "/services" ? "#ffffff" : "#94a3b8" }}
                onMouseEnter={e => (e.currentTarget.style.color = "#ffffff")}
                onMouseLeave={e => (e.currentTarget.style.color = href === "/services" ? "#ffffff" : "#94a3b8")}>
                {label}
              </Link>
            ))}
          </div>
          <div className="hidden lg:flex gap-3">
            <Link href="/contact">
              <Button className="font-semibold text-white" style={{ backgroundColor: "#6366f1" }}
                onMouseEnter={e => ((e.currentTarget as HTMLElement).style.backgroundColor = "#5558e3")}
                onMouseLeave={e => ((e.currentTarget as HTMLElement).style.backgroundColor = "#6366f1")}
                data-testid="button-services-enquire">
                Submit Enquiry
              </Button>
            </Link>
          </div>
          <button className="lg:hidden p-2" style={{ color: "#ffffff" }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="button-mobile-menu">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div style={{ backgroundColor: "#05070a", borderTop: "1px solid rgba(99,102,241,0.15)" }} className="p-4 space-y-3">
            {[
              { href: "/", label: "Home" },
              { href: "/about", label: "About" },
              { href: "/services", label: "Services" },
              { href: "/methodology", label: "Methodology" },
            ].map(({ href, label }) => (
              <Link key={label} href={href} className="block text-sm font-medium py-2"
                style={{ color: href === "/services" ? "#ffffff" : "#94a3b8" }}>
                {label}
              </Link>
            ))}
          </div>
        )}
      </nav>

      <section className="bg-slate-900 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-slate-400 uppercase tracking-widest text-xs mb-4 font-semibold">What We Do</p>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-6" data-testid="heading-services">
              Hiring Operations & Candidate Assessment Services
            </h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              We operate as an external execution layer alongside your internal team — absorbing hiring load,
              standardising early-stage evaluation, and delivering decision-ready candidate outputs.
            </p>
          </div>
        </div>
      </section>

      <section id="core-services" className="py-10 md:py-24" style={{ backgroundColor: '#05070a' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <p className="text-slate-400 uppercase tracking-widest text-xs mb-3 font-semibold">Core Services</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Three Core Capabilities
              </h2>
              <p className="text-base md:text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed">
                Every engagement is built on these three pillars — together they form a complete
                external hiring operations capability.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 mb-16">
              <Card className="p-8 border-white/10 bg-slate-800/30 hover:bg-slate-800/50 transition-all" data-testid="card-service-assessments">
                <div className="w-14 h-14 bg-slate-900 rounded-xl flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">Structured Candidate Assessments</h3>
                <p className="text-slate-400 mb-5 text-sm leading-relaxed">
                  Candidates complete structured, role-aligned assessments that produce consistent, comparable
                  evaluation data across your entire candidate pool.
                </p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Questions tailored to specific role outcomes</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Scenario, technical, and behavioural formats</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Completed remotely in under 10 minutes</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Bulk processing for 1,000 to 5,000+ candidates</span>
                  </div>
                </div>
                <div className="pt-5 border-t border-white/10">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Best For</p>
                  <p className="text-sm text-slate-300">
                    Employers and hiring managers who need a standardised way to evaluate large numbers of
                    applicants without sacrificing quality or consistency.
                  </p>
                </div>
              </Card>

              <Card className="p-8 border-white/10 bg-slate-800/30 hover:bg-slate-800/50 transition-all" data-testid="card-service-verification">
                <div className="w-14 h-14 bg-slate-900 rounded-xl flex items-center justify-center mb-6">
                  <BadgeCheck className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">Capability & Role-Fit Evaluation</h3>
                <p className="text-slate-400 mb-5 text-sm leading-relaxed">
                  Every candidate is evaluated against role-specific criteria. Credentials are assessed,
                  application consistency is reviewed, and capability signals are verified before reaching your desk.
                </p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Resume claim verification against responses</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">AI-written content detection</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Timeline and credential consistency checks</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Bias-free, audit-friendly scoring</span>
                  </div>
                </div>
                <div className="pt-5 border-t border-white/10">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Best For</p>
                  <p className="text-sm text-slate-300">
                    Organisations in regulated industries or high-trust roles where credential accuracy
                    and application integrity are non-negotiable.
                  </p>
                </div>
              </Card>

              <Card className="p-8 border-white/10 bg-slate-800/30 hover:bg-slate-800/50 transition-all" data-testid="card-service-dossiers">
                <div className="w-14 h-14 bg-slate-900 rounded-xl flex items-center justify-center mb-6">
                  <FileCheck className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">Decision-Ready Candidate Output</h3>
                <p className="text-slate-400 mb-5 text-sm leading-relaxed">
                  Receive structured candidate dossiers with capability scores, verification status,
                  and agent commentary — formatted for hiring committees and stakeholder review.
                </p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Ranked candidate profiles with scores</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Secure shareable links for stakeholders</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">PDF export for hiring committees</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Agent-reviewed before publication</span>
                  </div>
                </div>
                <div className="pt-5 border-t border-white/10">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Best For</p>
                  <p className="text-sm text-slate-300">
                    Executive teams, hiring committees, and board-level decision makers who need
                    clean, professional candidate summaries without sifting through raw data.
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section id="service-tiers" className="py-10 md:py-24" style={{ backgroundColor: '#0b0e17' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <p className="text-slate-400 uppercase tracking-widest text-xs mb-3 font-semibold">Managed Hiring Operations</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Service Tiers
              </h2>
              <p className="text-base md:text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed">
                Choose the level of operational support that matches your hiring volume and complexity.
                Every tier includes the full assessment engine — tiers differ by capacity, support depth,
                and dedicated operational resources.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              <Card className="p-8 border-white/10 bg-slate-800/30 relative" data-testid="card-tier-core">
                <div className="mb-6">
                  <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center mb-4">
                    <Shield className="w-6 h-6 text-slate-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-1">Core</h3>
                  <p className="text-sm text-slate-400">For teams hiring 1-3 roles per month</p>
                </div>
                <div className="space-y-3 mb-8">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Full assessment engine access</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Structured candidate assessments</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Capability & role-fit evaluation</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Decision-ready dossier output</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Employer dashboard access</span>
                  </div>
                </div>
                <div className="pt-6 border-t border-white/10">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Designed For</p>
                  <p className="text-sm text-slate-300 mb-4">
                    Small to mid-size companies with steady hiring needs who want to standardise their
                    candidate evaluation process and reduce time spent on unstructured reviews.
                  </p>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">How You Benefit</p>
                  <p className="text-sm text-slate-300">
                    Replace inconsistent resume reviews with structured assessments.
                    Receive ranked, verified candidates without adding internal workload.
                    No per-hire fees — predictable monthly cost.
                  </p>
                </div>
              </Card>

              <Card className="p-8 border-2 border-slate-900 bg-slate-800/30 relative" data-testid="card-tier-professional">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-slate-900 text-white text-xs font-semibold rounded-full">
                  Most Popular
                </div>
                <div className="mb-6">
                  <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center mb-4">
                    <Crown className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-1">Professional</h3>
                  <p className="text-sm text-slate-400">For teams hiring 4-8 roles per month</p>
                </div>
                <div className="space-y-3 mb-8">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Everything in Core</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Dedicated VEXIS agent</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Bulk candidate processing (CSV upload)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Priority assessment turnaround</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Overflow application management</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Hiring workflow support</span>
                  </div>
                </div>
                <div className="pt-6 border-t border-white/10">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Designed For</p>
                  <p className="text-sm text-slate-300 mb-4">
                    Growing companies experiencing regular hiring pressure — where recruitment agencies and talent acquisition teams are
                    stretched and need a reliable external partner to absorb execution load.
                  </p>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">How You Benefit</p>
                  <p className="text-sm text-slate-300">
                    A dedicated agent learns your culture and standards. Process thousands of applicants
                    through bulk invite without overloading your team. Overflow support activates
                    automatically during hiring surges.
                  </p>
                </div>
              </Card>

              <Card className="p-8 border-white/10 bg-slate-800/30 relative" data-testid="card-tier-enterprise">
                <div className="mb-6">
                  <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center mb-4">
                    <Building2 className="w-6 h-6 text-slate-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-1">Enterprise RPO</h3>
                  <p className="text-sm text-slate-400">For teams hiring 9+ roles per month</p>
                </div>
                <div className="space-y-3 mb-8">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Everything in Professional</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Multi-agent operational team</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Custom assessment frameworks</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Role intake & hiring alignment sessions</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Talent pipeline activation</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">Compliance & audit reporting</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-400">SLA-backed turnaround times</span>
                  </div>
                </div>
                <div className="pt-6 border-t border-white/10">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Designed For</p>
                  <p className="text-sm text-slate-300 mb-4">
                    Large organisations, multi-site operations, and enterprise HR teams running
                    continuous high-volume hiring across multiple business units or regions.
                  </p>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">How You Benefit</p>
                  <p className="text-sm text-slate-300">
                    A full operational team embedded alongside your internal function. Custom assessment
                    criteria built around your roles. Proactive pipeline building so you're never
                    waiting for candidates. Enterprise-grade compliance and audit trails.
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section id="overflow-modules" className="py-10 md:py-24" style={{ backgroundColor: '#05070a' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <p className="text-slate-400 uppercase tracking-widest text-xs mb-3 font-semibold">Optional Capacity Layers</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Hiring Overflow Support Modules
              </h2>
              <p className="text-base md:text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed">
                When hiring volume spikes beyond internal capacity, activate modular support layers
                to maintain operational stability — without adding permanent headcount.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="p-7 border-white/10 bg-slate-800/30 hover:bg-slate-800/50 transition-all" data-testid="card-module-overflow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Inbox className="w-6 h-6 text-slate-300" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">Overflow Application Management</h3>
                    <p className="text-slate-400 text-sm mb-4 leading-relaxed">
                      Absorb excess application volume during hiring surges. Your VEXIS team triages, processes,
                      and routes applications through the assessment engine so your internal team stays focused
                      on final decisions.
                    </p>
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Who It's For</p>
                      <p className="text-sm text-slate-300">
                        HR teams and hiring managers facing seasonal surges, rapid growth, or unexpected attrition
                        that pushes application volumes beyond manageable levels.
                      </p>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="p-7 border-white/10 bg-slate-800/30 hover:bg-slate-800/50 transition-all" data-testid="card-module-pipeline">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-slate-300" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">Talent Pipeline Activation</h3>
                    <p className="text-slate-400 text-sm mb-4 leading-relaxed">
                      Proactive sourcing support to build qualified candidate pipelines aligned to upcoming roles.
                      Candidates are pre-assessed and ready before you need them — reducing time-to-hire
                      when positions open.
                    </p>
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Who It's For</p>
                      <p className="text-sm text-slate-300">
                        Companies with planned growth, recurring role types, or hard-to-fill positions
                        who want candidates ready before headcount is approved.
                      </p>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="p-7 border-white/10 bg-slate-800/30 hover:bg-slate-800/50 transition-all" data-testid="card-module-workflow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-6 h-6 text-slate-300" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">Hiring Workflow Support</h3>
                    <p className="text-slate-400 text-sm mb-4 leading-relaxed">
                      Operational coverage for the coordination work that slows hiring teams down —
                      scheduling, candidate communication, process management, and stakeholder updates
                      handled by your VEXIS team.
                    </p>
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Who It's For</p>
                      <p className="text-sm text-slate-300">
                        Internal hiring teams spending too much time on coordination and admin,
                        reducing their capacity to focus on candidate quality and final decisions.
                      </p>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="p-7 border-white/10 bg-slate-800/30 hover:bg-slate-800/50 transition-all" data-testid="card-module-intake">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Target className="w-6 h-6 text-slate-300" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">Role Intake & Hiring Alignment</h3>
                    <p className="text-slate-400 text-sm mb-4 leading-relaxed">
                      Structured scoping sessions to define role requirements, success criteria,
                      and assessment parameters — ensuring alignment between what you need and
                      what the assessment engine evaluates.
                    </p>
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Who It's For</p>
                      <p className="text-sm text-slate-300">
                        Organisations creating new roles, entering new markets, or hiring for positions
                        where success criteria need to be carefully defined before assessments begin.
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-24" style={{ backgroundColor: '#0b0e17' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <p className="text-slate-400 uppercase tracking-widest text-xs mb-3 font-semibold">Who We Work With</p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Services by Client Type
              </h2>
              <p className="text-base md:text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed">
                Every client engages differently depending on their hiring challenges.
                Here's how each type benefits from the VEXIS platform.
              </p>
            </div>

            <div className="space-y-8">
              <Card className="p-8 border-white/10 bg-slate-800/30" data-testid="card-client-employer">
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-10">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-indigo-900/30 rounded-2xl flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-1">Employers & Hiring Teams</h3>
                    <p className="text-sm text-slate-400 mb-4">HR Directors, Talent Acquisition Leads, Hiring Managers, COOs</p>
                    <p className="text-slate-400 text-sm leading-relaxed mb-5">
                      You retain full decision authority — VEXIS handles the operational execution.
                      Receive ranked, verified candidate profiles delivered to your employer dashboard.
                      No more sifting through unstructured applications or losing candidates to slow processes.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-400">Dedicated employer dashboard with live status</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-400">Ranked candidate dossiers ready for review</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-400">Secure shareable links for stakeholders</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-400">No per-hire fees — predictable monthly pricing</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="p-8 border-white/10 bg-slate-800/30" data-testid="card-client-candidate">
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-10">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-emerald-900/30 rounded-2xl flex items-center justify-center">
                      <UserCheck className="w-8 h-8 text-emerald-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-1">Candidates</h3>
                    <p className="text-sm text-slate-400 mb-4">Professionals invited into the assessment process by their recruiter</p>
                    <p className="text-slate-400 text-sm leading-relaxed mb-5">
                      A structured, professional assessment experience that gives you the chance to demonstrate
                      your capabilities beyond a resume. Complete your assessment remotely, on your own schedule,
                      and know that your responses are reviewed fairly against consistent criteria.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-400">Complete assessments remotely in under 10 mins</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-400">Fair, bias-free evaluation criteria</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-400">Show capability beyond your resume</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-400">Professional, respectful candidate experience</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="p-8 border-white/10 bg-slate-800/30" data-testid="card-client-enterprise">
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-10">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-purple-900/30 rounded-2xl flex items-center justify-center">
                      <Globe className="w-8 h-8 text-purple-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-1">Enterprise & Multi-Site Operations</h3>
                    <p className="text-sm text-slate-400 mb-4">CHROs, Group HR, Regional Directors, Procurement Teams</p>
                    <p className="text-slate-400 text-sm leading-relaxed mb-5">
                      Centralised hiring operations across multiple business units, regions, or brands.
                      Standardise candidate evaluation across your entire organisation while maintaining
                      local flexibility. Enterprise compliance, audit trails, and SLA-backed delivery.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-400">Multi-agent team with centralised oversight</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-400">Custom assessment frameworks per division</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-400">Enterprise compliance and audit reporting</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-400">Procurement-safe, SLA-backed service terms</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-20 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to Streamline Your Assessment Process?
            </h2>
            <p className="text-lg text-slate-300 mb-8 leading-relaxed">
              Tell us about your hiring challenges and we'll show you how VEXIS can
              absorb the operational load — so you can focus on making the right decisions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/contact">
                <Button size="lg" className="gap-2 w-full sm:w-auto font-semibold text-white" style={{ backgroundColor: "#6366f1" }} data-testid="button-services-cta-enquire">
                  <Send className="w-4 h-4" />
                  Submit an Enquiry
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <TrustFooter />
    </div>
  );
}
